import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import WhatsAppButton from "@/components/WhatsAppButton";
import MatchRow from "@/components/MatchRow";
import BetSlipDock from "@/components/BetSlipDock";
import { Input } from "@/components/ui/input";
import { Search, Trophy, Flame } from "lucide-react";

const TABS = [
  { v: "all", label: "All" },
  { v: "live", label: "In-Play" },
  { v: "upcoming", label: "Upcoming" },
  { v: "completed", label: "Results" },
];

const Ticker = ({ items }) => {
  const row = items.map((m) => (
    <span key={m.id} className="mx-8 inline-flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-red-500 pulse-live" />
      <span className="font-semibold">{m.name}</span>
      {m.teams?.some((t) => t.score) && (
        <span className="font-mono-stat text-amber-300">{m.teams.map((t) => t.score || "–").join(" | ")}</span>
      )}
      <span className="text-zinc-500">{m.competition}</span>
    </span>
  ));
  return (
    <div className="bg-black border-b border-amber-500/15 overflow-hidden whitespace-nowrap text-xs py-1.5" data-testid="score-ticker">
      <div className="inline-block animate-[ticker_45s_linear_infinite]">
        {row}
        {row}
      </div>
    </div>
  );
};

const ago = (iso) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const nextIn = ({ odds_updated_at, refresh_every_seconds }) => {
  if (!odds_updated_at || !refresh_every_seconds) return null;
  const due = new Date(odds_updated_at).getTime() + refresh_every_seconds * 1000;
  const m = Math.max(0, Math.ceil((due - Date.now()) / 60000));
  return m === 0 ? "Updating…" : `Next in ${m}m`;
};

const HeroStat = ({ label, value, sub, accent, pulse, testid }) => (
  <div className="bg-black/60 backdrop-blur-xl border border-amber-500/25 rounded-lg px-4 py-3 min-w-[110px]" data-testid={testid}>
    <div className="text-[10px] font-display uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
      {pulse && <span className="h-1.5 w-1.5 rounded-full bg-red-500 pulse-live" />}
      {label}
    </div>
    <div className={`font-mono-stat text-xl sm:text-2xl mt-0.5 whitespace-nowrap ${accent}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 whitespace-nowrap" data-testid={`${testid}-next`}>{sub}</div>}
  </div>
);

export default function Landing() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const [all, setAll] = useState({ items: [], source: "odds" });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [comp, setComp] = useState("all");
  const [pick, setPick] = useState(null);
  const [, tick] = useState(0);

  useEffect(() => {
    const load = () =>
      api
        .get("/cricket/matches?status=all")
        .then((r) => setAll(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    load();
    const poll = setInterval(load, 60000);
    const clock = setInterval(() => tick((n) => n + 1), 15000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, []);

  const comps = useMemo(() => [...new Set(all.items.map((m) => m.competition).filter(Boolean))], [all]);
  const live = all.items.filter((m) => m.status === "live");
  const items = all.items.filter((m) => {
    if (tab !== "all" && m.status !== tab) return false;
    if (comp !== "all" && m.competition !== comp) return false;
    const text = `${m.name} ${m.competition}`.toLowerCase();
    return !q || text.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#09090D] text-zinc-100">
      <style>{`@keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
      <Header />
      {live.length > 0 && <Ticker items={live} />}

      <section className="relative overflow-hidden border-b border-amber-500/20">
        <div
          className="absolute inset-0 scale-105"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/36230651/pexels-photo-36230651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090D] via-[#09090D]/90 to-[#09090D]/40" />
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 py-10 sm:py-14 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="animate-[fadeUp_0.6s_ease-out]">
            <div className="text-[11px] font-display uppercase tracking-[0.4em] text-amber-400/80 mb-3">Har Har Mahadev</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-none">
              <span className="gold-text">BholeBook</span>
            </h1>
            <div className="mt-3 font-display text-xl sm:text-2xl uppercase tracking-[0.2em] text-amber-100" data-testid="hero-tagline">
              24x7 India's Best Book
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 animate-[fadeUp_0.8s_ease-out]">
            <HeroStat label="In-Play" value={live.length} accent="text-red-400" pulse testid="hero-inplay" />
            <HeroStat label="Markets" value={all.items.length} accent="text-amber-300" testid="hero-markets" />
            <HeroStat
              label="Odds Updated"
              value={all.odds_updated_at ? ago(all.odds_updated_at) : "—"}
              sub={nextIn(all)}
              accent="text-emerald-300"
              testid="hero-odds-updated"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-2 sm:px-6 py-6 grid grid-cols-12 gap-4">
        <aside className="hidden lg:block col-span-2" data-testid="sports-sidebar">
          <div className="bg-zinc-950 border border-amber-500/20 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-zinc-900 text-amber-400 font-display uppercase tracking-widest text-xs">Sports</div>
            <button className="w-full text-left px-3 py-2 text-sm bg-amber-400/10 text-amber-300 border-l-2 border-amber-400 flex items-center gap-2" data-testid="sport-cricket">
              <Trophy className="h-3.5 w-3.5" /> Cricket
            </button>
            <div className="px-3 py-2 bg-zinc-900 text-amber-400 font-display uppercase tracking-widest text-xs mt-1">Competitions</div>
            {["all", ...comps].map((c) => (
              <button
                key={c}
                onClick={() => setComp(c)}
                data-testid={`comp-${c}`}
                className={`w-full text-left px-3 py-2 text-xs border-b border-zinc-900 transition-colors ${comp === c ? "text-amber-300 bg-zinc-900" : "text-zinc-300 hover:bg-zinc-900"}`}
              >
                {c === "all" ? "All Competitions" : c}
              </button>
            ))}
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-7">
          <div className="bg-zinc-950 border border-amber-500/20 rounded-md overflow-hidden">
            <div className="flex flex-wrap items-center gap-1 bg-zinc-900 px-2 py-2">
              {TABS.map((t) => (
                <button
                  key={t.v}
                  onClick={() => setParams({ tab: t.v })}
                  data-testid={`tab-${t.v}`}
                  className={`px-3 py-1.5 text-xs font-display uppercase tracking-widest rounded transition-colors ${tab === t.v ? "bg-amber-400 text-black" : "text-zinc-300 hover:bg-zinc-800"}`}
                >
                  {t.v === "live" && <Flame className="inline h-3 w-3 mr-1" />}
                  {t.label}
                </button>
              ))}
              <div className="relative ml-auto w-full sm:w-56 mt-2 sm:mt-0">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-zinc-500" />
                <Input
                  data-testid="match-search-input"
                  placeholder="Search events"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-8 pl-8 text-xs bg-black border-amber-500/20"
                />
              </div>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
              <div className="col-span-5">Match Odds</div>
              <div className="col-span-7 grid grid-cols-3 text-center">
                <span>1</span><span>X</span><span>2</span>
              </div>
            </div>
            {loading && [1, 2, 3, 4].map((i) => <div key={i} className="h-14 border-b border-zinc-800 animate-pulse bg-zinc-900/40" />)}
            {!loading && items.length === 0 && (
              <div className="py-16 text-center" data-testid="empty-state">
                <div className="font-display uppercase tracking-widest text-amber-400">No markets open right now</div>
                <div className="text-xs text-zinc-500 mt-2">New cricket markets appear automatically as bookmakers open them.</div>
              </div>
            )}
            {!loading && items.map((m) => <MatchRow key={m.id} m={m} onPick={setPick} />)}
          </div>
        </main>

        <aside className="col-span-12 lg:col-span-3">
          <BetSlipDock pick={pick} onClear={() => setPick(null)} />
        </aside>
      </div>

      <footer className="border-t border-amber-500/10 py-8 text-center text-xs font-display uppercase tracking-widest text-zinc-500">
        BHOLEBOOK · 24x7 India's Best Book
      </footer>
      <WhatsAppButton />
    </div>
  );
}
